import { Close, Edit, Psychology, Send } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import SearchableSenderProfileSelect, {
  loadAllSenderProfiles,
} from "../../components/SearchableSenderProfileSelect";
import { hostname, parseError } from "../../utils";

export default ({
  open,
  onClose,
  message,
  getToken,
  eventListener,
  companyId,
  onReplySent,
  onManageProfiles,
}: {
  open: boolean;
  onClose: () => void;
  message: any;
  getToken: Function;
  eventListener: Function;
  companyId: string;
  onReplySent?: () => void;
  onManageProfiles?: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);

  // Form state
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");
  const [editInstructions, setEditInstructions] = useState("");

  // Options from API
  const [profiles, setProfiles] = useState<Array<any>>([]);

  // Original message context
  const [originalSequence, setOriginalSequence] = useState<any>(null);

  const handleClose = () => {
    setSubject("");
    setContent("");
    setSelectedProfile("");
    setEditInstructions("");
    setOriginalSequence(null);
    onClose();
  };

  const loadProfiles = async () => {
    setProfilesLoading(true);
    const currentToken = await getToken();
    try {
      const allProfiles = await loadAllSenderProfiles(currentToken, companyId);
      setProfiles(allProfiles);

      // Extract email from recipient field (format: "Name <email@domain.com>")
      let recipientEmail: string | null = null;
      if (message.recipient) {
        const emailMatch = message.recipient.match(/<([^>]+)>/);
        recipientEmail = emailMatch ? emailMatch[1] : message.recipient;
      }

      // Try to find matching profile: first by recipient email, then by inbox domain
      let matchedProfile = null;

      if (recipientEmail) {
        matchedProfile = allProfiles.find(
          (profile: any) => profile.email === recipientEmail
        );
      }

      if (!matchedProfile && message.inbox?.domain) {
        matchedProfile = allProfiles.find(
          (profile: any) => profile.email === message.inbox.domain
        );
      }

      if (matchedProfile) {
        setSelectedProfile(matchedProfile.id);
      }
    } catch (err) {
      console.error("Error loading profiles:", err);
    } finally {
      setProfilesLoading(false);
    }
  };

  const findOriginalSequence = async () => {
    // Only fetch sequence info if this is a reply to a sequence execution
    if (!message?.replyClassification?.sequenceExecutionId) {
      return;
    }

    const currentToken = await getToken();

    try {
      // Get the sequence execution details to find the original sequence
      const res = await axios.get(
        `${hostname}/sequences/executions/${message.replyClassification.sequenceExecutionId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (res.data) {
        setOriginalSequence(res.data);

        // PRIORITY 1: Try to use the execution's sender profile ID (the actual sender used for this execution)
        if (res.data.senderProfileId) {
          setSelectedProfile(res.data.senderProfileId);
          console.log(
            "Using execution's sender profile:",
            res.data.senderProfileId
          );
        }
      }
    } catch (err) {
      console.error("Error finding original sequence:", err);
    }
  };

  const generateReply = async () => {
    if (!selectedProfile) {
      eventListener({
        type: "SET_ERROR",
        error: "Please select a sender profile.",
      });
      return;
    }

    setGenerating(true);
    const currentToken = await getToken();

    try {
      const res = await axios.post(
        `${hostname}/inbox/messages/${message.id}/generate-reply`,
        {
          companyId,
          senderProfileId: selectedProfile,
          originalContent: message.body,
          originalSubject: message.subject,
          replyClassification: message.replyClassification,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (res.data.subject) {
        setSubject(res.data.subject);
      }
      if (res.data.content) {
        setContent(res.data.content);
      }
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setGenerating(false);
    }
  };

  const editReplyWithAI = async () => {
    if (!editInstructions.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Please provide instructions for editing the reply.",
      });
      return;
    }

    if (!content.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Please generate or write a reply first before editing.",
      });
      return;
    }

    setEditing(true);
    const currentToken = await getToken();

    try {
      const res = await axios.post(
        `${hostname}/inbox/messages/${message.id}/edit-reply`,
        {
          companyId,
          currentSubject: subject,
          currentContent: content,
          editInstructions: editInstructions.trim(),
          originalContent: message.body,
          originalSubject: message.subject,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (res.data.subject) {
        setSubject(res.data.subject);
      }
      if (res.data.content) {
        setContent(res.data.content);
      }

      // Clear edit instructions after successful edit
      setEditInstructions("");

      eventListener({
        type: "SET_SUCCESS",
        message: "Reply edited successfully!",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setEditing(false);
    }
  };

  const sendReply = async () => {
    if (!subject.trim() || !content.trim() || !selectedProfile) {
      eventListener({
        type: "SET_ERROR",
        error: "Please fill in all required fields",
      });
      return;
    }

    setSending(true);
    const currentToken = await getToken();

    try {
      await axios.post(
        `${hostname}/inbox/messages/${message.id}/reply`,
        {
          companyId,
          subject: subject.trim(),
          content: content.trim(),
          senderProfileId: selectedProfile,
          replyToMessageId: message.id,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      eventListener({
        type: "SET_SUCCESS",
        message: "Reply sent successfully!",
      });

      // Call the callback to refresh the messages list
      if (onReplySent) {
        onReplySent();
      }

      handleClose();
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (open && message) {
      setLoading(true);
      Promise.all([loadProfiles(), findOriginalSequence()]).finally(() => {
        setLoading(false);
      });

      // Set default subject (Re: original subject)
      if (message.subject && !message.subject.toLowerCase().startsWith("re:")) {
        setSubject(`Re: ${message.subject}`);
      } else {
        setSubject(message.subject || "Re: ");
      }

      // Load pre-generated reply if it exists
      if (message.generatedReply) {
        setContent(message.generatedReply);
      }
    }
  }, [open, message]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Reply to Message</Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Original Message Context */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Original Message
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>From:</strong>{" "}
            {message?.senderName ? `${message.senderName} ` : ""}
            {message?.sender}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Subject:</strong> {message?.subject}
          </Typography>
          {message?.replyClassification && (
            <Typography variant="body2" color="text.secondary">
              <strong>Classification:</strong>{" "}
              {message.replyClassification.category}(
              {message.replyClassification.sentiment},{" "}
              {message.replyClassification.confidence.toFixed(0)}% confident)
            </Typography>
          )}
          {originalSequence && (
            <Typography variant="body2" color="text.secondary">
              <strong>Original Sequence:</strong>{" "}
              {originalSequence.sequence?.name}
            </Typography>
          )}
        </Paper>

        {/* Sender Profile Selection */}
        <Box sx={{ mb: 3 }}>
          <SearchableSenderProfileSelect
            profiles={profiles}
            selectedId={selectedProfile}
            onChangeSingle={(id) => setSelectedProfile(id)}
            label="Sender Profile"
            helperText="Select the sender profile to reply from."
            onManageProfiles={onManageProfiles}
            loading={profilesLoading}
          />
        </Box>

        {/* AI Generate Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Psychology />}
            onClick={generateReply}
            disabled={generating || !selectedProfile}
            sx={{ mr: 2 }}
          >
            {generating ? "Generating..." : "Generate AI Reply"}
          </Button>
          {generating && <LinearProgress sx={{ mt: 1 }} />}
        </Box>

        {/* AI Edit Section */}
        {content && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Edit Instructions (e.g., 'Make it more professional', 'Add a call to action')"
              value={editInstructions}
              onChange={(e) => setEditInstructions(e.target.value)}
              placeholder="Tell AI how to modify the reply..."
              sx={{ mb: 1 }}
              multiline
              rows={2}
            />
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={editReplyWithAI}
              disabled={editing || !editInstructions.trim()}
            >
              {editing ? "Editing..." : "Edit with AI"}
            </Button>
            {editing && <LinearProgress sx={{ mt: 1 }} />}
          </Box>
        )}

        {/* Subject Field */}
        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
          required
        />

        {/* Content Field */}
        {message?.generatedReply && (
          <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
            ✨ Auto-generated reply loaded. You can edit it before sending.
          </Typography>
        )}
        <TextField
          fullWidth
          label="Message Content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          multiline
          rows={8}
          sx={{ mb: 3 }}
          required
        />

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={sendReply}
            disabled={
              sending || !subject.trim() || !content.trim() || !selectedProfile
            }
          >
            {sending ? "Sending..." : "Send Reply"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
